import { domain } from "../lib/domain";
import type { Person } from "../lib/presence";
// Pipeline grid columns: display, raw (edit/copy) text and parsing back into a PATCH `set`.
import type { EditableField, Loan, LoanSet, Settings, Stage } from "../lib/types";
import { amountPlain, money, moneyCompact, price3, pts3 } from "../lib/format";
import { dateShort, parseDate } from "../lib/dates";
import { FORECAST_LABEL, LOCK_LABEL, matchForecast, matchLock, matchStage } from "../lib/stages";
import { num } from "../lib/decimal";

export type ColKind =
  | "file"
  | "owner"
  | "money"
  | "tier"
  | "stage"
  | "date"
  | "lock"
  | "price"
  | "derived"
  | "text"
  | "alerts"
  | "longtext";

export interface ColCtx {
  stages: Stage[];
  stageByKey: Map<string, Stage>;
  settings: Settings;
  today: string;
  compact: boolean;
  presence?: Map<string, Person[]>;
  userId?: string;
}

export type ParseResult = { ok: true; set: LoanSet } | { ok: false; message: string };

export interface ColSpec {
  id: string;
  title: string;
  width: number;
  kind: ColKind;
  field?: EditableField;
  align?: "right";
  display: (l: Loan, c: ColCtx) => string;
  raw: (l: Loan, c: ColCtx) => string;
  parse?: (raw: string, l: Loan, c: ColCtx) => ParseResult;
  help?: string;
}

const str = (v: string | null | undefined) => v ?? "";

function dateRaw(v: string | null): string {
  if (!v) return "";
  return `${Number(v.slice(5, 7))}/${Number(v.slice(8, 10))}/${v.slice(0, 4)}`;
}

export function parseAmount(raw: string, allowZero = false): { ok: true; value: string | null } | { ok: false; message: string } {
  const s = raw.trim().toLowerCase().replace(/[$,\s]/g, "");
  if (s === "" || s === "-") return { ok: true, value: null };
  const m = /^(\d+(?:\.\d+)?|\.\d+)([km])?$/.exec(s);
  if (!m) return { ok: false, message: `Couldn't read "${raw}" as an amount. Try 850k or 1,250,000.` };
  const multiplier = m[2] === "k" ? "1000" : m[2] === "m" ? "1000000" : "1";
  return domain<{ ok: true; value: string } | { ok: false; message: string }>({ kind: "amount", amount: m[1], multiplier, allow_zero: allowZero });
}

function textField(field: EditableField, nullable = true) {
  return (raw: string): ParseResult => {
    const v = raw.replace(/\s+/g, " ").trim();
    return { ok: true, set: { [field]: v === "" && nullable ? null : v } };
  };
}

function dateField(field: EditableField) {
  return (raw: string, _l: Loan, c: ColCtx): ParseResult => {
    const d = parseDate(raw, c.today);
    if (d.ok) return { ok: true, set: { [field]: d.value } };
    if (field === "lock_expires_on") {
      const lock = matchLock(raw);
      if (lock !== undefined) return { ok: true, set: { lock_status: lock } };
    }
    return { ok: false, message: d.message };
  };
}

export function buildColumns(opts: { showOwner: boolean; showComp: boolean; compact: boolean }): ColSpec[] {
  const { compact } = opts;
  const cols: ColSpec[] = [
    {
      id: "file_name",
      title: "File name",
      width: compact ? 180 : 210,
      kind: "file",
      field: "file_name",
      display: (l) => l.file_name,
      raw: (l) => l.file_name,
      parse: textField("file_name", false),
    },
  ];
  if (opts.showOwner) {
    cols.push({
      id: "owner",
      title: "AE",
      width: 110,
      kind: "owner",
      display: (l) => l.owner_name,
      raw: (l) => l.owner_name,
    });
  }
  cols.push(
    { id: "presence", title: "Here now", width: 120, kind: "derived",
      display: (l, c) => (c.presence?.get(l.id) ?? []).filter((p) => p.user_id !== c.userId).map((p) => `${p.name}${p.field ? ` (${p.field})` : ""}`).join(", "),
      raw: (l, c) => (c.presence?.get(l.id) ?? []).filter((p) => p.user_id !== c.userId).map((p) => `${p.name}${p.field ? ` (${p.field})` : ""}`).join(", "),
    },
    {
      id: "amount",
      title: "Amount",
      width: compact ? 96 : 118,
      kind: "money",
      field: "amount",
      align: "right",
      display: (l, c) => (c.compact ? moneyCompact(l.amount) : amountPlain(l.amount)),
      raw: (l) => (l.amount ? String(num(l.amount)) : ""),
      parse: (raw) => {
        const a = parseAmount(raw);
        return a.ok ? { ok: true, set: { amount: a.value } } : a;
      },
    },
    {
      id: "forecast",
      title: "Tier",
      width: compact ? 74 : 86,
      kind: "tier",
      field: "forecast",
      display: (l) => (l.forecast ? FORECAST_LABEL[l.forecast] : ""),
      raw: (l) => str(l.forecast),
      parse: (raw) => {
        const f = matchForecast(raw);
        return f === undefined ? { ok: false, message: `"${raw}" isn't a tier (PIN, MB, High, TBD)` } : { ok: true, set: { forecast: f } };
      },
    },
    {
      id: "stage_key",
      title: "Stage",
      width: compact ? 130 : 186,
      kind: "stage",
      field: "stage_key",
      display: (l, c) => (l.stage_key ? (c.stageByKey.get(l.stage_key)?.label ?? l.stage_key) : ""),
      raw: (l, c) => (l.stage_key ? (c.stageByKey.get(l.stage_key)?.label ?? l.stage_key) : ""),
      parse: (raw, _l, c) => {
        const s = matchStage(raw, c.stages);
        return s === undefined ? { ok: false, message: `"${raw}" isn't a stage` } : { ok: true, set: { stage_key: s ? s.key : null } };
      },
    },
    {
      id: "coe_date",
      title: "COE",
      width: 78,
      kind: "date",
      field: "coe_date",
      display: (l, c) => dateShort(l.coe_date, c.today),
      raw: (l) => dateRaw(l.coe_date),
      parse: dateField("coe_date"),
    },
    {
      id: "lock_status",
      title: "Lock",
      width: 92,
      kind: "lock",
      field: "lock_status",
      display: (l) => (l.lock_status ? LOCK_LABEL[l.lock_status] : ""),
      raw: (l) => (l.lock_status ? LOCK_LABEL[l.lock_status] : ""),
      parse: (raw, _l, c) => {
        const s = matchLock(raw);
        if (s !== undefined) return { ok: true, set: { lock_status: s } };
        const d = parseDate(raw, c.today);
        if (d.ok && d.value) return { ok: true, set: { lock_status: "locked", lock_expires_on: d.value } };
        return { ok: false, message: `"${raw}" isn't a lock status (Locked, Floating, N/A)` };
      },
    },
    {
      id: "lock_expires_on",
      title: "Lock exp.",
      width: 84,
      kind: "date",
      field: "lock_expires_on",
      display: (l, c) => dateShort(l.lock_expires_on, c.today),
      raw: (l) => dateRaw(l.lock_expires_on),
      parse: dateField("lock_expires_on"),
    },
    {
      id: "last_cd_date",
      title: "Last CD",
      width: 78,
      kind: "date",
      field: "last_cd_date",
      display: (l, c) => dateShort(l.last_cd_date, c.today),
      raw: (l) => dateRaw(l.last_cd_date),
      parse: dateField("last_cd_date"),
    },
    {
      id: "signing_date",
      title: "Signing",
      width: 78,
      kind: "date",
      field: "signing_date",
      display: (l, c) => dateShort(l.signing_date, c.today),
      raw: (l) => dateRaw(l.signing_date),
      parse: dateField("signing_date"),
    },
    {
      id: "price",
      title: "Price",
      width: 92,
      kind: "price",
      field: "price",
      align: "right",
      display: (l) => price3(l.price),
      raw: (l) => price3(l.price),
      parse: (raw) => ({ ok: true, set: { price: raw.trim() === "" ? null : raw.trim() } }),
      help: "Type 102.75, 2.75 (points) or 1.0275 — all save as 102.750",
    },
    {
      id: "rev_pct",
      title: "Rev %",
      width: 74,
      kind: "derived",
      align: "right",
      display: (l) => (l.rev_pct === null ? "" : `${pts3(l.rev_pct)}%`),
      raw: (l) => (l.rev_pct === null ? "" : pts3(l.rev_pct)),
      help: "Rev % = price − 100",
    },
    {
      id: "revenue_amount",
      title: "Rev $",
      width: compact ? 84 : 98,
      kind: "derived",
      align: "right",
      display: (l, c) => (c.compact ? moneyCompact(l.revenue_amount) : money(l.revenue_amount)),
      raw: (l) => (l.revenue_amount === null ? "" : String(num(l.revenue_amount))),
      help: "Rev $ = amount × (price − 100) / 100",
    },
  );
  if (opts.showComp) {
    cols.push({
      id: "comp",
      title: "Comp $",
      width: 88,
      kind: "derived",
      align: "right",
      display: (l) => (l.comp ? (l.comp.commission ? money(l.comp.commission) : "needs price") : ""),
      raw: (l) => (l.comp?.commission ? String(num(l.comp.commission)) : ""),
      help: "Your commission on this loan (bps × amount)",
    });
  }
  cols.push(
    {
      id: "broker",
      title: "Broker / LO",
      width: 150,
      kind: "text",
      field: "broker",
      display: (l) => str(l.broker),
      raw: (l) => str(l.broker),
      parse: textField("broker"),
    },
    {
      id: "loan_type",
      title: "Loan type",
      width: 116,
      kind: "text",
      field: "loan_type",
      display: (l) => str(l.loan_type),
      raw: (l) => str(l.loan_type),
      parse: textField("loan_type"),
    },
    {
      id: "loan_number",
      title: "Loan #",
      width: 142,
      kind: "text",
      field: "loan_number",
      display: (l) => str(l.loan_number),
      raw: (l) => str(l.loan_number),
      parse: textField("loan_number"),
    },
    {
      id: "alerts",
      title: "Alerts",
      width: 72,
      kind: "alerts",
      display: (l) => l.alerts.filter((a) => a.severity !== "info").map((a) => a.message).join("; "),
      raw: (l) => l.alerts.map((a) => a.message).join("; "),
    },
    {
      id: "notes",
      title: "Notes",
      width: 260,
      kind: "longtext",
      field: "notes",
      display: (l) => str(l.notes).replace(/\s*\n\s*/g, " · "),
      raw: (l) => str(l.notes),
      parse: (raw) => ({ ok: true, set: { notes: raw.trim() === "" ? null : raw } }),
    },
  );
  return cols;
}

/** Compare a parsed set with the loan; drop fields that would not change anything. */
export function changedOnly(set: LoanSet, l: Loan): LoanSet {
  const out: LoanSet = {};
  for (const [f, v] of Object.entries(set) as [EditableField, LoanSet[EditableField]][]) {
    const cur = l[f as keyof Loan] as unknown;
    if (f === "amount" || f === "note_rate") {
      if (num(cur as string | null) === num(v as string | null)) continue;
    } else if (f === "price") {
      if (v === null && cur === null) continue;
      if (typeof v === "string" && cur !== null && num(v) === num(cur as string)) continue;
    } else if ((cur ?? null) === (v ?? null)) continue;
    out[f] = v ?? null;
  }
  return out;
}
